import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';

export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 2.5;
export const ZOOM_STEP = 0.1;

const clampZoom = (value: number): number => {
	const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
	return Math.round(clamped * 10) / 10;
};

const MAX_PAGE_PILLS_BEFORE_SCROLL = 6;

export interface DocumentToolbarProps {
	zoomScale: number;
	onZoomScaleChange: (scale: number) => void;
	currentPageIndex: number;
	numPages: number;
	onPageChange: (pageIndex: number) => void;
}

const PillButton: React.FC<{
	onClick?: () => void;
	disabled?: boolean;
	'aria-label': string;
	children: React.ReactNode;
}> = ({ onClick, disabled, children, ...rest }) => (
	<button
		type="button"
		onClick={onClick}
		disabled={disabled}
		className="flex items-center justify-center disabled:opacity-30 hover:bg-[#F4F4F4] transition-colors shrink-0"
		style={{ width: 28, height: 28 }}
		{...rest}
	>
		{children}
	</button>
);

export const DocumentToolbar: React.FC<DocumentToolbarProps> = ({
	zoomScale,
	onZoomScaleChange,
	currentPageIndex,
	numPages,
	onPageChange,
}) => {
	const thumbPercent =
		((clampZoom(zoomScale) - ZOOM_MIN) / (ZOOM_MAX - ZOOM_MIN)) * 100;

	const handleZoomOut = (): void =>
		onZoomScaleChange(clampZoom(zoomScale - ZOOM_STEP));
	const handleZoomIn = (): void =>
		onZoomScaleChange(clampZoom(zoomScale + ZOOM_STEP));
	const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
		onZoomScaleChange(clampZoom(Number(e.target.value)));

	const isFirstPage = currentPageIndex <= 0;
	const isLastPage = currentPageIndex >= numPages - 1;

	const pageNumbers = Array.from({ length: numPages }, (_, i) => i);
	const scrollsHorizontally = numPages > MAX_PAGE_PILLS_BEFORE_SCROLL;
	const pillsRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		const strip = pillsRef.current;
		if (!scrollsHorizontally || !strip) return;
		const activePill = strip.querySelector<HTMLElement>(
			'[aria-current="true"]'
		);
		if (!activePill) return;
		const stripRect = strip.getBoundingClientRect();
		const pillRect = activePill.getBoundingClientRect();
		const pillCenter =
			pillRect.left - stripRect.left + strip.scrollLeft + pillRect.width / 2;
		strip.scrollTo({
			left: pillCenter - strip.clientWidth / 2,
			behavior: 'smooth',
		});
	}, [currentPageIndex, scrollsHorizontally]);

	return (
		<div
			className="flex items-center justify-between shrink-0"
			style={{
				padding: '8px 32px',
				background: '#FFFFFF',
				borderBottom: '1px solid rgba(13, 6, 22, 0.06)',
			}}
		>
			<div className="flex items-center gap-2.5">
				<div
					className="flex items-center"
					style={{
						padding: '6px 12px',
						gap: 12,
						background: '#F4F4F4',
						border: '1px solid rgba(13, 6, 22, 0.08)',
						boxShadow: '0px 4px 12px 0px rgba(8, 15, 52, 0.06)',
						borderRadius: 9999,
					}}
				>
					<button
						type="button"
						onClick={handleZoomOut}
						disabled={zoomScale <= ZOOM_MIN}
						aria-label="Zoom out"
						className="flex items-center justify-center disabled:opacity-30 shrink-0"
					>
						<Icon
							src={Icons.minus}
							width={16}
							height={16}
							className="text-[#6E6A73]"
						/>
					</button>

					<div className="relative shrink-0" style={{ width: 64, height: 16 }}>
						<div
							className="absolute left-0 right-0 top-1/2 -translate-y-1/2"
							style={{
								height: 2,
								background: '#D9D9D9',
								borderRadius: 9999,
							}}
						/>
						<div
							className="absolute top-1/2 pointer-events-none"
							style={{
								left: `calc(${thumbPercent}% - 4px)`,
								width: 8,
								height: 8,
								background: '#8039DF',
								borderRadius: 9999,
								transform: 'translateY(-50%)',
							}}
						/>
						<input
							type="range"
							min={ZOOM_MIN}
							max={ZOOM_MAX}
							step={ZOOM_STEP}
							value={zoomScale}
							onChange={handleRangeChange}
							aria-label="Zoom level"
							className="absolute inset-0 w-full h-full opacity-0 cursor-pointer m-0"
						/>
					</div>

					<button
						type="button"
						onClick={handleZoomIn}
						disabled={zoomScale >= ZOOM_MAX}
						aria-label="Zoom in"
						className="flex items-center justify-center disabled:opacity-30 shrink-0"
					>
						<Icon
							src={Icons.plus2}
							width={16}
							height={16}
							className="text-[#6E6A73]"
						/>
					</button>
				</div>
			</div>

			<div className="flex items-center gap-2 shrink-0">
				<Typography
					type="regular-12"
					className="!text-[12px] text-[#6E6A73] tabular-nums shrink-0"
				>
					Page {numPages === 0 ? 0 : currentPageIndex + 1} of {numPages}
				</Typography>

				<PillButton
					onClick={() => !isFirstPage && onPageChange(currentPageIndex - 1)}
					disabled={isFirstPage}
					aria-label="Previous page"
				>
					<ChevronLeftIcon className="size-3.5 text-[#6E6A73]" />
				</PillButton>

				<div
					ref={pillsRef}
					className={
						scrollsHorizontally
							? 'flex items-center gap-1 overflow-x-auto hide-scroll-bar'
							: 'flex items-center gap-1'
					}
					style={scrollsHorizontally ? { maxWidth: 168 } : undefined}
				>
					{pageNumbers.map((pageIndex) => {
						const isActive = pageIndex === currentPageIndex;
						return (
							<button
								key={pageIndex}
								type="button"
								onClick={() => onPageChange(pageIndex)}
								aria-label={`Go to page ${pageIndex + 1}`}
								aria-current={isActive ? 'true' : undefined}
								className="flex items-center justify-center rounded-full shrink-0 transition-colors"
								style={{
									width: 28,
									height: 28,
									background: isActive ? '#8039DF' : '#F4F4F4',
								}}
							>
								<Typography
									type="semibold-12"
									className={
										isActive
											? '!text-[11px] text-white'
											: '!text-[11px] text-[#6E6A73]'
									}
								>
									{pageIndex + 1}
								</Typography>
							</button>
						);
					})}
				</div>

				<PillButton
					onClick={() => !isLastPage && onPageChange(currentPageIndex + 1)}
					disabled={isLastPage}
					aria-label="Next page"
				>
					<ChevronRightIcon className="size-3.5 text-[#6E6A73]" />
				</PillButton>
			</div>
		</div>
	);
};

export default DocumentToolbar;
